import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Container,
  Paper,
  Button,
  ImageList,
  ImageListItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../../../contexts/AuthContext';
import MarinaAdminLayout from '../../../../layouts/MarinaAdminLayout';
import { getImages, uploadImage, deleteImage, updateImageMetadata, ImageCategory, IMAGE_CATEGORIES, ImageMetadata } from '../../../../firebase/services/imageService';

const ImageManager: React.FC<{
  category: ImageCategory;
  showPublicOnly: boolean;
}> = ({ category, showPublicOnly }) => {
  const [images, setImages] = useState<ImageMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editImage, setEditImage] = useState<ImageMetadata | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editAltText, setEditAltText] = useState('');
  const [editTags, setEditTags] = useState<string>('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);

  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const imgs = await getImages(category, showPublicOnly);
      setImages(imgs);
    } catch (err: any) {
      setError(err.message || 'Failed to load images');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchImages();
    setSelectedIds([]);
     
  }, [category, showPublicOnly]);

  const handleDelete = async (ids: string[]) => {
    if (!window.confirm(`Delete ${ids.length > 1 ? 'these images' : 'this image'}?`)) return;
    setDeleting(true);
    try {
      for (const id of ids) {
        await deleteImage(id);
      }
      fetchImages();
      setSelectedIds([]);
    } catch (err: any) {
      setError(err.message || 'Failed to delete image(s)');
    }
    setDeleting(false);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setError(null);
    try {
      await uploadImage(uploadFile, category, { title: uploadTitle });
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadTitle('');
      fetchImages();
    } catch (err: any) {
      setError(err.message || 'Failed to upload image');
    }
    setUploading(false);
  };

  const handleEdit = (image: ImageMetadata) => {
    setEditImage(image);
    setEditTitle(image.title || '');
    setEditAltText(image.altText || '');
    setEditTags((image.tags || []).join(','));
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editImage) return;
    try {
      await updateImageMetadata(editImage.id, {
        title: editTitle,
        altText: editAltText,
        tags: editTags.split(',').map(t => t.trim()).filter(Boolean)
      });
      setEditDialogOpen(false);
      setEditImage(null);
      fetchImages();
    } catch (err: any) {
      setError(err.message || 'Failed to update image metadata');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{category.charAt(0) + category.slice(1).toLowerCase().replace('_', ' ')} Images</Typography>
        <Box>
          {selectedIds.length > 0 && (
            <Button color="error" variant="outlined" startIcon={<DeleteIcon />} sx={{ mr: 2 }} onClick={() => handleDelete(selectedIds)} disabled={deleting}>
              Delete Selected
            </Button>
          )}
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUploadDialogOpen(true)}>
            Upload Image
          </Button>
        </Box>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />
      ) : images.length === 0 ? (
        <Alert severity="info">No images found in this category.</Alert>
      ) : (
        <ImageList cols={4} gap={16} sx={{ maxHeight: 500 }}>
          {images.map((image) => (
            <ImageListItem key={image.id}>
              <Box sx={{ position: 'relative' }}>
                <Checkbox
                  checked={selectedIds.includes(image.id)}
                  onChange={() => toggleSelect(image.id)}
                  sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2, bgcolor: 'white', borderRadius: '50%' }}
                />
                <img src={image.storageUrl} alt={image.title || image.filename} loading="lazy" style={{ maxHeight: 160, objectFit: 'cover', width: '100%' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" noWrap>{image.title || image.filename}</Typography>
                  <Box>
                    <IconButton size="small" color="primary" onClick={() => handleEdit(image)}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete([image.id])}><DeleteIcon /></IconButton>
                  </Box>
                </Box>
                {image.tags && image.tags.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {image.tags.map(tag => <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5 }} />)}
                  </Box>
                )}
              </Box>
            </ImageListItem>
          ))}
        </ImageList>
      )}
      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload Image</DialogTitle>
        <DialogContent>
          <input type="file" accept="image/*" onChange={e => setUploadFile(e.target.files?.[0] || null)} />
          <TextField
            label="Title (optional)"
            fullWidth
            margin="normal"
            value={uploadTitle}
            onChange={e => setUploadTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!uploadFile || uploading}>{uploading ? 'Uploading...' : 'Upload'}</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Metadata Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Image Metadata</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
          />
          <TextField
            label="Alt Text"
            fullWidth
            margin="normal"
            value={editAltText}
            onChange={e => setEditAltText(e.target.value)}
          />
          <TextField
            label="Tags (comma separated)"
            fullWidth
            margin="normal"
            value={editTags}
            onChange={e => setEditTags(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ImageManagementPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  if (!userProfile) {
    return (
      <MarinaAdminLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            You must be logged in to access this page
          </Alert>
        </Box>
      </MarinaAdminLayout>
    );
  }

  // Convert object keys to array of keys with type safety
  const categories = Object.keys(IMAGE_CATEGORIES) as ImageCategory[];

  return (
    <MarinaAdminLayout>
      <Container maxWidth="xl">
        <Paper sx={{ p: 3, my: 3 }}>
          <Typography variant="h4" gutterBottom>
            Image Management
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Manage images for your marina, vessels, and other content. You can upload, edit, and delete images here.
          </Typography>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={selectedTab} onChange={handleTabChange}>
              {categories.map((category, index) => (
                <Tab
                  key={category}
                  label={category.charAt(0) + category.slice(1).toLowerCase().replace('_', ' ')}
                  id={`image-tab-${index}`}
                />
              ))}
            </Tabs>
          </Box>

          {categories.map((category, index) => (
            <Box
              key={category}
              role="tabpanel"
              hidden={selectedTab !== index}
              id={`image-tabpanel-${index}`}
            >
              {selectedTab === index && (
                <ImageManager
                  category={category}
                  showPublicOnly={false}
                />
              )}
            </Box>
          ))}
        </Paper>
      </Container>
    </MarinaAdminLayout>
  );
};

export default ImageManagementPage; 